import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Rocket, Plug, Binary, LogIn, Send, Undo2, KeyRound, TerminalSquare, AlertTriangle } from 'lucide-react';
import { cardEngineApi, cardInstitutionApi } from '../../services/iso8583Api';
import { useAuth } from '../../contexts/AuthContext';
import { isAdmin } from '../../utils/roles';
import { PageHeader, Card, CodeBlock, Table, cardLink, CardEndpointNote, useDialect, useSignOnSample, originalElement, originalHow } from './common';

/**
 * The id the participant's gateway sends in DE 32. The Sandbox assigns one with
 * the corridor; an admin replaces it with the institution's own.
 */
function InstitutionIdPanel({ inst, error, onSaved }) {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => { if (inst) setDraft(inst.institutionIds.join(', ')); }, [inst]);

  const parsed = draft.split(/[\s,]+/).filter(Boolean);
  const dirty = inst && parsed.join(',') !== inst.institutionIds.join(',');

  const save = async () => {
    setBusy(true); setSaveError(null); setSaved(false);
    try { onSaved(await cardInstitutionApi.replace(parsed)); setSaved(true); }
    catch (e) { setSaveError(e.response?.data?.message || e.message); }
    finally { setBusy(false); }
  };

  const ids = inst?.institutionIds || [];
  return (
    <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <p className="text-sm font-semibold text-gray-900 mb-1">Your institution id</p>
      {!inst && !error && <p className="text-sm text-gray-500">Reading your institution id…</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}
      {inst && (ids.length ? (
        <p className="text-sm text-gray-700">
          Your gateway sends <span className="font-mono font-semibold">{ids.join(', ')}</span> in DE 32 when it signs on.
          {inst.sandboxAssigned && <> The Sandbox assigned {ids.length > 1 ? 'these' : 'this id'} so you can sign on straight
            away. If your institution has its own acquiring institution id, {admin ? 'replace it here' : 'ask an admin at your institution to replace it'}.</>}
        </p>
      ) : (
        <p className="text-sm text-amber-700">
          No institution id yet, so the switch will refuse your sign-on. {admin
            ? 'Enter your institution\'s id below.' : 'Ask an admin at your institution to set one.'}
        </p>
      ))}
      {inst && admin && (
        <div className="mt-3">
          <div className="flex items-center gap-2 flex-wrap">
            <input value={draft} onChange={(e) => { setDraft(e.target.value); setSaved(false); }} placeholder="e.g. 483912"
              aria-label="Institution id"
              className="flex-1 min-w-[12rem] px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm font-mono focus:ring-2 focus:ring-primary-500" />
            <button type="button" onClick={save} disabled={!dirty || !parsed.length || busy}
              className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-40">
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">1 to 11 digits; separate several with commas. An id can belong to one participant only.</p>
          {saved && <p className="text-xs text-green-700 mt-1">Saved. The sign-on on this page now carries {ids.join(', ')}.</p>}
          {saveError && <p className="text-xs text-red-600 mt-1">{saveError}</p>}
        </div>
      )}
    </div>
  );
}

/**
 * From nothing to a certified card link: connect, frame, sign on, transact,
 * recover — each step with what the switch expects on the wire.
 */
export default function CardGettingStarted() {
  const [health, setHealth] = useState(null);
  useEffect(() => { cardEngineApi.health().then(setHealth).catch(() => setHealth(null)); }, []);
  const [inst, setInst] = useState(null);
  const [instError, setInstError] = useState(null);
  const [idsVersion, setIdsVersion] = useState(0);
  useEffect(() => {
    cardInstitutionApi.mine()
      .then((r) => {
        // Anything but the engine's answer must not read as "no id".
        if (!Array.isArray(r?.institutionIds)) throw new Error('unexpected answer');
        setInst(r);
      })
      .catch((e) => setInstError(e.response?.data?.message || 'Your institution id could not be read just now.'));
  }, []);
  const d = useDialect();
  const { mti, nm, rc, code } = d;
  const fn = (key) => nm.codes?.[key] ?? '?';
  const { sample, explanation } = useSignOnSample(idsVersion);
  const signOnHex = sample?.frameHex || '';
  const institution = inst?.institutionIds?.[0] || '<your institution id>';

  const endpoint = cardLink(health);
  const { host, port, tls } = endpoint;
  // Which dialect is in force decides what this page may claim: the baseline's
  // shapes are only the baseline's, and a vendor profile replaces them.
  const dialect = health?.dialect || 'generic-ascii-1987';
  const baseline = dialect === 'generic-ascii-1987';


  // Proving the port is reachable, before any code is written. On a TLS link
  // telnet gets as far as "Connected" and no further — which is still the
  // answer to the question it was asked.
  const connectivity = tls
    ? `# 1 · is the port reachable from this machine?
telnet ${host} ${port}
nc -vz ${host} ${port}          # the same answer, without the interactive session

# 2 · is it the card switch? complete a TLS handshake for this name
openssl s_client -connect ${host}:${port} -servername ${host} </dev/null 2>/dev/null \\
  | grep -E 'subject=|Verify return code'`
    : `telnet ${host} ${port}
nc -vz ${host} ${port}          # the same answer, without the interactive session`;

  // The whole of a sign-on: open a socket, frame one message, read one back.
  const signOnPython = `import datetime, socket, ssl, struct

HOST, PORT = "${host}", ${port}
USE_TLS     = ${tls ? 'True' : 'False'}            # this deployment's card link
INSTITUTION = "${institution}"   # what you send in DE 32

def frame(message: bytes) -> bytes:
    """Two bytes, big-endian, counting the message but not themselves."""
    return struct.pack(">H", len(message)) + message

# The sign-on the Sandbox built for your institution on this dialect — the same
# bytes the Message Generator's "Sign on" template produces, prefix included.
# DE 11 inside it is a trace number: give every later message its own.
SIGN_ON = bytes.fromhex("${signOnHex || '<generate one in the Message Generator>'}")

def read_frame(sock) -> bytes:
    def exactly(n: int) -> bytes:
        buf = b""
        while len(buf) < n:
            chunk = sock.recv(n - len(buf))
            if not chunk:
                raise ConnectionError("the switch closed the connection")
            buf += chunk
        return buf
    return exactly(struct.unpack(">H", exactly(2))[0])   # prefix, then that many

sock = socket.create_connection((HOST, PORT), timeout=10)
if USE_TLS:                             # by hostname: the Sandbox routes on it
    ctx = ssl.create_default_context()
    sock = ctx.wrap_socket(sock, server_hostname=HOST)

sock.sendall(SIGN_ON)
reply = read_frame(sock)                # ${nm.responseMti}: the correlation fields echoed, DE ${rc.field} the answer
print(reply.hex(" "))                   # the reply frame, byte for byte — paste it into Explain a Frame
# DE ${rc.field} = ${code('approved')} signed on · ${code('institutionUnknown')} not enrolled here

# Keep this socket open and use it for transactions. After 60 s of quiet the
# switch sends an echo (${nm.requestMti} / DE ${nm.field} = ${fn('echoTest')}); answer it or the link is closed.`;

  // The same sign-on without writing a file: the frame the engine built, as
  // printf escapes, straight onto the socket.
  const signOnShell = tls
    ? `{ printf '${sample?.printf || '<generate a sign-on in the Message Generator>'}'; sleep 2; } \\
  | openssl s_client -quiet -no_ign_eof -connect ${host}:${port} -servername ${host} 2>/dev/null \\
  | xxd`
    : `{ printf '${sample?.printf || '<generate a sign-on in the Message Generator>'}'; sleep 2; } \\
  | nc ${host} ${port} \\
  | xxd`;

  return (
    <div className="space-y-6 max-w-4xl">
      <PageHeader icon={Rocket} title="Getting Started — ISO 8583">
        Connect your card gateway to the Sandbox's card switch, as you would to Postilion, SmartVista or Base24:
        a persistent TCP link, length-prefixed ISO 8583 messages, sign-on first.
      </PageHeader>

      <Card title="1 · Connect" icon={Plug}>
        <CardEndpointNote endpoint={endpoint} className="mb-3" />
        <Table head={['', '']} rows={[
          ['Card link', endpoint.published
            ? <span className="font-mono">{host}:{port}{tls ? ' (TLS)' : ''}</span>
            : <span className="text-amber-800">not published by this Sandbox</span>],
          ['Status', health ? (health.status === 'UP' ? 'listening' : 'down — the card link is not accepting connections') : 'unknown'],
          ['Transport', tls
            ? <>One persistent TCP connection inside TLS 1.2 or 1.3; you may open more. Connect <strong>by the hostname</strong>, not
                an IP address: the Sandbox shares its port with the portal and finds the card switch by the name your gateway sends
                in the TLS handshake (SNI). The switch presents the same server certificate as this portal, for your gateway to verify;
                no client certificate is asked for — your link is identified by the institution id you sign on with.</>
            : 'One persistent TCP connection; you may open more. No TLS on this deployment\'s card link.'],
          ['Framing', <>A 2-byte big-endian length before every message, counting the message but not itself ({health?.framing || 'postilion'} framing).</>],
          ['Dialect', baseline
            ? <>ISO 8583:1987, ASCII baseline ({dialect}): ASCII MTI and numeric fields, 8-byte binary bitmaps,
                LLVAR lengths as two ASCII digits.</>
            : <><span className="font-mono">{dialect}</span> — a profile AfricaNenda supplied for this deployment, so the
                field table is its own. See <Link to="/iso8583/dialect" className="text-primary-600 hover:underline">Dialect</Link>.</>],
        ]} />
        <InstitutionIdPanel inst={inst} error={instError}
          onSaved={(r) => { setInst(r); setIdsVersion((v) => v + 1); }} />

        <div className="mt-5 pt-4 border-t border-gray-100">
          <p className="text-sm font-semibold text-gray-900 mb-1">Is the switch listening?</p>
          <p className="text-sm text-gray-700 mb-3">
            Before writing any code, prove the port is reachable from the machine your gateway runs on. This is the first
            thing to check when a link will not come up, and it separates a network problem from a message problem.
          </p>
          <CodeBlock>{connectivity}</CodeBlock>
          <Table head={['What you see', 'What it means']} rows={tls ? [
            [<span className="font-mono">Connected to {host}</span>,
              <>The port is open. Nothing further happens: the switch is waiting for a TLS handshake, which telnet does not
                speak — leave with <span className="font-mono">Ctrl+]</span> then <span className="font-mono">quit</span>. That
                is as far as telnet can take you on a TLS link.</>],
            [<span className="font-mono">Verify return code: 0 (ok)</span>,
              <>The handshake finished, so the Sandbox accepted this hostname and routed it. Your gateway must send the same
                name (SNI), which it does automatically when it connects by hostname rather than by IP address.</>],
            [<span className="font-mono">Connection refused</span>,
              'Nothing is listening there. Check the host and port above, and that your firewall allows the outbound connection.'],
            ['It waits, and never says Connected',
              'Something in between is dropping the connection — a firewall, or a hostname pointing somewhere else.'],
          ] : [
            [<span className="font-mono">Connected to {host}</span>,
              <>The port is open. telnet cannot sign on, though: a frame begins with two <em>binary</em> length bytes, which a
                keyboard cannot type — use the sign-on below. Leave with <span className="font-mono">Ctrl+]</span> then <span className="font-mono">quit</span>.</>],
            [<span className="font-mono">Connection refused</span>,
              'Nothing is listening there. Check the host and port above, and that your firewall allows the outbound connection.'],
            ['It waits, and never says Connected',
              'Something in between is dropping the connection — a firewall, or a hostname pointing somewhere else.'],
          ]} />
          <p className="text-xs text-gray-500 mt-3">
            An open port is not proof that the switch is well: the card link answers only ISO 8583. The sign-on below is the
            real check — and this page&apos;s <strong>Status</strong> row above reports the switch&apos;s own health.
          </p>
        </div>
      </Card>

      <Card title="2 · Sign on" icon={LogIn}>
        <p className="text-sm text-gray-700 mb-3">
          Nothing but network management is accepted until the link signs on. Send a <span className="font-mono">{nm.requestMti}</span> with
          {d.defines(7) ? ' DE 7, DE 11,' : ' DE 11,'} DE {nm.field} = <span className="font-mono">{fn('signOn')}</span> and your institution id in DE 32; the switch answers
          <span className="font-mono"> {nm.responseMti}</span> with DE {rc.field} = <span className="font-mono">{code('approved')}</span>. A <span className="font-mono">{code('institutionUnknown')}</span> says the id is
          not enrolled for you on this corridor.
        </p>
        <p className="text-xs text-gray-500 mb-2">
          Your sign-on, byte for byte as it goes on the socket — built by the switch's own codec for your institution
          id, in the dialect in force:
        </p>
        {sample ? <CodeBlock>{sample.dump || sample.frameHex}</CodeBlock>
                : <p className="text-xs text-gray-400 mb-2">Building it…</p>}
        {explanation?.fields?.length > 0 && (
          <Table head={['Element', 'Value', 'What it is']} mono={[0, 1]} rows={[
            ['MTI', sample?.mti, `${nm.requestMti} — network management request`],
            ...explanation.fields.map((f) => [`DE ${f.de}`, f.value, [f.name, f.meaning].filter(Boolean).join(' — ')]),
          ]} />
        )}
        <div className="mt-5 pt-4 border-t border-gray-100">
          <p className="text-sm font-semibold text-gray-900 mb-1">Open the socket and send it</p>
          <p className="text-sm text-gray-700 mb-3">
            This is the whole of a sign-on in any language: open a TCP socket{tls ? ', wrap it in TLS' : ''}, put the
            2-byte length in front of the message, write it, then read the reply&apos;s own length and that many bytes.
            Nothing here is specific to Python.
          </p>
          <CodeBlock>{signOnPython}</CodeBlock>
          <p className="text-sm text-gray-700 mt-4 mb-2">
            Or from a shell, without writing a file — the same bytes:
          </p>
          <CodeBlock>{signOnShell}</CodeBlock>
          <p className="text-xs text-gray-500 mt-2">
            The reply begins <span className="font-mono">{nm.responseMti}</span> and carries DE {rc.field} ={' '}
            <span className="font-mono">{code('approved')}</span> when the sign-on was accepted. Paste it into{' '}
            <Link to="/iso8583/explain" className="text-primary-600 hover:underline">Explain a Frame</Link> to read every
            element; the <Link to="/iso8583/message-generator" className="text-primary-600 hover:underline">Message Generator</Link>{' '}
            rebuilds this sign-on with a fresh trace number whenever you need one.
          </p>
        </div>

        <p className="text-xs text-gray-500 mt-3">
          Keep the link alive: after 60 seconds of quiet the switch sends an echo (<span className="font-mono">{nm.requestMti}</span> / DE {nm.field} = {fn('echoTest')}).
          Answer it with <span className="font-mono">{nm.responseMti}</span> echoing {d.defines(7) ? 'DE 7, 11' : 'DE 11'} and {nm.field} — a link that does not answer within 30 seconds is closed.
        </p>
      </Card>

      <Card title="3 · Send transactions" icon={Send}>
        <p className="text-sm text-gray-700 mb-3">
          The switch answers as a scripted test issuer: a test card is approved, and the cents of the amount choose a decline, a lost
          reply or a lost acknowledgement — see <Link to="/iso8583/test-cards" className="text-primary-600 hover:underline">Test Cards</Link>.
          A certifiable request carries:
        </p>
        <Table head={['Message', 'Required fields']} mono={[0]} rows={[
          [`${mti('0100')} / ${mti('0200')}`, `DE 2 (or DE 35), ${d.only([3, 4, 7, 11, 12, 13, 22, 41, 49]).join(', ')}`],
          [mti('0420'), `DE ${d.only([4, 7, 11, 90]).join(', ')}`],
        ]} />
        <p className="text-xs text-gray-500 mt-3">
          Every new transaction needs its own STAN (DE 11){d.defines(7) ? ' and DE 7' : ''}. The same {d.defines(7) ? 'STAN and DE 7' : 'STAN'} again, without the repeat indicator,
          is answered <span className="font-mono">{code('duplicateTransaction')}</span> and never decided twice.
        </p>
      </Card>

      <Card title="4 · Recover when a message is lost" icon={Undo2}>
        <p className="text-sm text-gray-700 mb-3">
          The switch cannot tell a lost request from a lost reply, and neither can you — so both sides reverse. When no
          <span className="font-mono"> {mti('0210')}</span> arrives in time, send a <span className="font-mono">{mti('0420')}</span> reversal advice
          that names the original in {originalElement(d.originalData)}:
        </p>
        {d.originalData.layout === 'iso1987' && (
          <Table head={['DE 90 positions', 'Content']} mono={[0]} rows={[
            ['1–4', `original MTI, e.g. ${mti('0200')}`],
            ['5–10', 'original STAN (DE 11)'],
            ['11–20', 'original transmission date and time (DE 7)'],
            ['21–31', 'original acquiring institution (DE 32), right-justified, zero-filled'],
            ['32–42', 'original forwarding institution (DE 33), zero-filled — zeros when absent'],
          ]} />
        )}
        {d.originalData.layout === 'iso1993' && (
          <Table head={[`DE ${d.originalData.field} positions`, 'Content']} mono={[0]} rows={[
            ['1–4', `original MTI, e.g. ${mti('0200')}`],
            ['5–10', 'original STAN (DE 11)'],
            ['11–22', 'original local date and time (DE 12)'],
            ['23–24', 'the length of the acquirer id that follows'],
            ['25–', 'original acquiring institution (DE 32)'],
          ]} />
        )}
        {d.originalData.layout === 'fields' && (
          <Table head={['Element', 'In the reversal']} mono={[0]} rows={[
            ...(d.originalData.match || []).map((de) => [`DE ${de}`, "the original's value, carried as it was"]),
            ['DE 11', "the reversal's own STAN"],
          ]} />
        )}
        <p className="text-xs text-gray-500 mt-3">
          {originalHow(d.originalData).replace(/^the/, 'The')}. The switch acknowledges every reversal with{' '}
          <span className="font-mono">{mti('0430')}</span>, matched or not. An unacknowledged advice is repeated
          with the repeat indicator — <span className="font-mono">{mti('0421')}</span> — same {d.defines(7) ? 'STAN and DE 7' : 'STAN'}. A repeated request (<span className="font-mono">{mti('0201')}</span>) of something
          already answered gets the original answer back.
        </p>
      </Card>

      <Card title="5 · Message authentication (when your link uses it)" icon={KeyRound}>
        <p className="text-sm text-gray-700">
          Optional on this switch. When enabled for your link: ANSI X9.9 (DES CBC-MAC) with a shared, non-production test key,
          over the values of DE 2, 3, 4, 11 and 41 concatenated in that order, carried in DE 128 as the 4-byte code followed by
          four spaces. Network management is never MAC'd. A missing or wrong MAC is answered <span className="font-mono">{code('formatError')}</span>.
          Compare key check values (KCV) with AfricaNenda rather than exchanging keys in the clear.
        </p>
      </Card>

      <Card title="6 · Two tools for when it does not work" icon={TerminalSquare}>
        <p className="text-sm text-gray-700 mb-3">
          Both are here in the portal, and both speak whatever dialect this switch runs — so what they show is what it reads.
        </p>
        <Table head={['', '']} rows={[
          [<Link to="/iso8583/message-generator" className="text-primary-600 hover:underline">Message Generator</Link>,
            <>Build any message for this dialect, see it as bytes and as ASCII, and send it on a link the Sandbox opens for
              you — then copy the same bytes into your own gateway. Nothing sent there is scored; your gateway's traffic is.</>],
          [<Link to="/iso8583/explain" className="text-primary-600 hover:underline">Explain a Frame</Link>,
            <>Paste a frame from your own log — or from a trace of the switch you are joining — and read it back: every
              element named, both bitmaps, and each finding with the byte it was found at. Nothing is sent.</>],
          [<Link to="/iso8583/dialect" className="text-primary-600 hover:underline">Dialect</Link>,
            'The field table and framing this switch expects, as the switch itself reports them.'],
        ]} />
      </Card>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-2">
        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-900">
          Use only the published test card numbers. A card number outside the test ranges is refused with 14 and never processed —
          but it still crosses the network, so never send a real one.
        </p>
      </div>
    </div>
  );
}
